import { faCircleExclamation } from '@fortawesome/free-solid-svg-icons';
import { Button, TextField } from '@tryhackme/thm-ui-components';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';
import { useForm } from 'src/common/form';
import { useBackNavigation } from 'src/common/hooks/use-back-navigation';
import { StyledFullWidthButton } from '../components/login-form/login-form.styles';
import { StyledErrorBox, StyledErrorIcon, StyledFooterWrapper, StyledForm, StyledLoginHeader, StyledLoginPageCard, StyledLoginPageWrapper, StyledLoginSubtitle } from './sso.styles';
export const SingleSignOnLogin = () => {
  const [searchParams] = useSearchParams();
  const loginHint = searchParams.get('loginHint') ?? undefined;
  const {
    register,
    handleSubmit,
    watch,
    formState: {
      touchedFields
    }
  } = useForm<{
    email: string;
  }>({
    defaultValues: {
      email: loginHint
    },
    mode: 'onChange'
  });
  const emailValue = watch('email');
  const navigate = useNavigate();
  const navigateBack = useBackNavigation();
  const [isLoading, setIsLoading] = useState(false);
  const [hasOrgError, setHasOrgError] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const isValidEmail = (email: string) => {
    const emailRegex = /^[\w%+.-]+@[\d.A-Za-z-]+\.[A-Za-z]{2,}$/;
    return emailRegex.test(email);
  };
  const submitForm = ({
    email
  }: {
    email: string;
  }) => {
    setHasOrgError(false);
    setEmailError(null);
    if (!isValidEmail(email)) {
      setEmailError('The email you entered is not valid. Please enter a valid email address to continue.');
      return;
    }
    setIsLoading(true);
    navigate(`/api/v2/auth/sso?email=${email}`);
  };
  useEffect(() => {
    const error = searchParams.get('error');
    setHasOrgError(!touchedFields.email && error === 'no-org');
  }, [searchParams, touchedFields]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (emailError) {
      setEmailError(null);
    }
  }, [emailValue]);
  const errorRef = useRef<HTMLDivElement | null>(null);
  return <StyledLoginPageWrapper data-sentry-element="StyledLoginPageWrapper" data-sentry-component="SingleSignOnLogin" data-sentry-source-file="sso.tsx">
      <StyledLoginPageCard data-sentry-element="StyledLoginPageCard" data-sentry-source-file="sso.tsx">
        <StyledLoginHeader data-sentry-element="StyledLoginHeader" data-sentry-source-file="sso.tsx">TryHackMe Login with SSO</StyledLoginHeader>
        <StyledLoginSubtitle data-sentry-element="StyledLoginSubtitle" data-sentry-source-file="sso.tsx">Enter your company email to authenticate using single sign-on (SSO).</StyledLoginSubtitle>
        <CSSTransition in={hasOrgError || !!emailError} nodeRef={errorRef} timeout={300} classNames="error-box" unmountOnExit data-sentry-element="CSSTransition" data-sentry-source-file="sso.tsx">
          <StyledErrorBox ref={errorRef} data-sentry-element="StyledErrorBox" data-sentry-source-file="sso.tsx">
            <StyledErrorIcon icon={faCircleExclamation} data-sentry-element="StyledErrorIcon" data-sentry-source-file="sso.tsx" />
            <p>
              {emailError || 'Your account is not configured to use SSO. Please log in or sign up with a username and password.'}
            </p>
          </StyledErrorBox>
        </CSSTransition>

        {/* eslint-disable-next-line @typescript-eslint/no-misused-promises */}
        <StyledForm onSubmit={handleSubmit(submitForm)} $isError={hasOrgError || !!emailError} data-sentry-element="StyledForm" data-sentry-source-file="sso.tsx">
          <TextField {...register('email')} label="Company email" id="company-email-input" type="text" autoComplete="email" variant="input" placeholder="Example@example.com" data-sentry-element="TextField" data-sentry-source-file="sso.tsx" />
          <StyledFullWidthButton color="primary" variant="solid" type="submit" isDisabled={isLoading} isLoading={isLoading} data-sentry-element="StyledFullWidthButton" data-sentry-source-file="sso.tsx">
            Log In
          </StyledFullWidthButton>
          <StyledFooterWrapper data-sentry-element="StyledFooterWrapper" data-sentry-source-file="sso.tsx">
            <p>
              Not using SSO?{' '}
              <Button variant="text" color="info" onClick={navigateBack} data-sentry-element="Button" data-sentry-source-file="sso.tsx">
                Go back
              </Button>
            </p>
          </StyledFooterWrapper>
        </StyledForm>
      </StyledLoginPageCard>
    </StyledLoginPageWrapper>;
};